/** Wire mirror stays inside the frontend-only Docker build boundary. */
export type ClinicalQuestionTerm =
  | { readonly source: 'entered' | 'unknown'; readonly label: string }
  | { readonly source: 'option'; readonly code: string; readonly label: string };
export type ClinicalQuestionDetails = { readonly version: 1; readonly legacyText: string } & (
  { readonly kind: 'allergies'; readonly allergies: readonly { readonly key: string; readonly substance: ClinicalQuestionTerm; readonly reactions: readonly ClinicalQuestionTerm[]; readonly notes: string }[] }
  | { readonly kind: 'conditions' | 'medications'; readonly terms: readonly ClinicalQuestionTerm[] }
);
export type AllergyDetailDraft = { key: string; substance: ClinicalQuestionTerm | null; reactions: ClinicalQuestionTerm[]; notes: string };
export type ClinicalQuestionDetailDraft = { version: 1; legacyText: string } & (
  { kind: 'allergies'; allergies: AllergyDetailDraft[] }
  | { kind: 'conditions' | 'medications'; terms: ClinicalQuestionTerm[] }
);

export function clinicalQuestionKind(key: string): ClinicalQuestionDetails['kind'] | null {
  return key === 'allergies' ? 'allergies' : key === 'medications' ? 'medications' : key === 'relevant_conditions' ? 'conditions' : null;
}
export const clinicalTermIdentity = (term: ClinicalQuestionTerm): string => JSON.stringify([term.source,term.source==='option'?term.code:term.label]);
export function hydrateClinicalDetails(kind: ClinicalQuestionDetails['kind'], legacyText: string, saved?: ClinicalQuestionDetails | null): ClinicalQuestionDetailDraft {
  const first = (): AllergyDetailDraft[] => legacyText ? [] : [{ key: 'first', substance: null, reactions: [], notes: '' }];
  if (saved?.kind === kind) {
    if (saved.kind === 'allergies') return { ...saved, allergies: saved.allergies.length ? saved.allergies.map(row => ({ ...row, reactions: [...row.reactions] })) : first() };
    return { ...saved, terms: [...saved.terms] };
  }
  return kind === 'allergies' ? { version: 1, kind, legacyText, allergies: first() } : { version: 1, kind, legacyText, terms: [] };
}
/** Blank optional rows are UI only. A reaction/note without its substance is
 * never silently dropped on save, even after all suggestion queries are clear. */
export function serializeClinicalDetails(draft: ClinicalQuestionDetailDraft): ClinicalQuestionDetails | null {
  if (draft.kind !== 'allergies') return { ...draft, terms: draft.terms.map(term => ({ ...term })) };
  if (draft.allergies.some(row => row.substance === null && (row.reactions.length > 0 || row.notes.trim().length > 0))) return null;
  return { ...draft, allergies: draft.allergies.flatMap(row => row.substance === null ? [] : [{ ...row, substance: row.substance, reactions: [...row.reactions] }]) };
}
export function clinicalDetailSummary(value: ClinicalQuestionDetails, includeOptionLabels = true): string | null {
  const parts = [value.legacyText];
  if (value.kind === 'allergies') parts.push(...value.allergies.map(row => `${row.substance.label} — ${row.reactions.length ? row.reactions.map(t => t.label).join(', ') : 'Reaction not recorded'}${row.notes ? ` · ${row.notes}` : ''}`));
  else parts.push(...value.terms.filter(t => includeOptionLabels || t.source !== 'option').map(t => t.label));
  return parts.filter(part => part.trim()).join('; ') || null;
}

export function clinicalDetailsPending(draft: ClinicalQuestionDetailDraft, queries: Readonly<Record<string,string>>): boolean {
  if (serializeClinicalDetails(draft) === null) return true;
  if (draft.kind !== 'allergies') return !!queries.terms?.trim();
  return draft.allergies.some(row => !!queries[`${row.key}:reactions`]?.trim() ||
    (queries[`${row.key}:substance`] !== undefined && queries[`${row.key}:substance`]!.trim() !== (row.substance?.label.trim() ?? '')));
}

/** Configured Other still requires actual free detail, not a coded Other chip. */
export function clinicalDetailsSatisfyRequiredDetail(value: ClinicalQuestionDetails): boolean {
  return !!value.legacyText.trim() || (value.kind==='allergies' ? value.allergies.length>0 : value.terms.some(term=>term.source!=='option'));
}


/** Approved display search aliases, tied to the unchanged configured names.
 * They do not create codes, infer an answer or provide a clinical master. */
export function clinicalTermSearchLabels(term:ClinicalQuestionTerm):readonly string[]{
 if(term.source==='option' && term.code==='diabetes' && term.label==='Diabetes')return [term.label,'sugar'];
 if(term.source==='option' && term.code==='blood_pressure' && term.label==='Blood pressure')return [term.label,'bp'];
 return [term.label];
}
